import { FC, useCallback, useState, useMemo } from 'react';
import { SampleDefinitionRowData } from '../SamplePlanTableProvider.tsx';
import { RegisteredAnimalTarget, SampleDefinitionApiId } from '@/model/SamplePlan.model.ts';
import { errorToast, successToast } from '@/helpers.tsx';
import Button from '@/components/UI/Button';
import { ModalContainer, ModalHeader } from '@/utils/modal.tsx';
import { AnimalSelectionTable } from './AnimalSelectionTable.tsx';
import { StudyApiId } from '@/model/Study.model.ts';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { AnimalForSampling, calculateNumberOfSamples } from './AnimalSelectionTable.utils.ts';
import Banner from '@/components/UI/Banner';
import { _isEmpty, _isNil, _notNil } from '@/littledash.ts';
import { isApiId } from '@/utils/ApiId.utils.ts';
import { AnimalApiId } from '@/model/Animal.model.ts';
import { AnimalDefinitionApiId } from '@/model/PlannedAnimal.model.ts';

interface CreatePlannedSamplesModalProps {
  studyApiId: StudyApiId;
  sampleDefinitions: Array<SampleDefinitionRowData>;
  closeModal: () => void;
  handleCallback: () => void;
}

export const CreatePlannedSamplesModal: FC<CreatePlannedSamplesModalProps> = ({
  studyApiId,
  sampleDefinitions,
  closeModal,
  handleCallback,
}) => {
  const [selectedAnimals, setSelectedAnimals] = useState<Set<RegisteredAnimalTarget['api_id']>>(new Set());
  const [animalsData, setAnimalsData] = useState<Array<AnimalForSampling>>([]);

  // Calculate total samples that will be created based on each selected animal's specific sample groups
  const totalSamplesToCreate = useMemo(() => {
    if (_isEmpty(animalsData) || _isEmpty(selectedAnimals)) {
      return 0;
    }

    const animalMap: Record<string, AnimalForSampling> = animalsData.reduce<Record<string, AnimalForSampling>>(
      (map, animal) => {
        map[animal.api_id] = animal;
        return map;
      },
      {}
    );

    return Array.from(selectedAnimals).reduce((total, animalId) => {
      const animal: AnimalForSampling | undefined = animalMap[animalId as string];
      if (!animal?.sample_groups) {
        return total;
      }
      // Calculate samples for this specific animal based on its sample groups
      const samplesForThisAnimal = calculateNumberOfSamples(animal.sample_groups);
      return total + samplesForThisAnimal;
    }, 0);
  }, [selectedAnimals, animalsData]);

  const { loading: isCreating, invoke: executeCreateSamples } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/planned/samples/execute',
    path: { studyApiId },
    invokeOnInit: false,
    options: {
      onError: { toast: true, slug: 'planned-sample-execution' },
    },
  });

  const handleCreateSamples = useCallback(() => {
    if (_isNil(studyApiId) || _isEmpty(selectedAnimals.size)) {
      errorToast('Please select at least one animal to create samples for.');
      return;
    }

    // Build the request body with proper animal-sample definition matching
    const sampleDefinitionsMap: Record<string, Array<AnimalApiId>> = {};

    // Create lookup map for animal data for O(1) access
    const animalMap: Record<string, AnimalForSampling> = animalsData.reduce<Record<string, AnimalForSampling>>(
      (map, animal) => {
        map[animal.api_id] = animal;
        return map;
      },
      {}
    );

    // Create map of animalApiId => animalDefinitionApiId for planned animals
    const animalIdDefinitionMap = new Map<AnimalApiId, AnimalDefinitionApiId>();
    animalsData.forEach((animal) => {
      if (_notNil(animal.animal_definition_api_id)) {
        animalIdDefinitionMap.set(animal.api_id, animal.animal_definition_api_id);
      }
    });

    sampleDefinitions.forEach((sampleDef) => {
      // Filter selected animals to only those that have this sample definition in their sample_groups
      // and are valid AnimalApiIds (not temporary IDs)
      const eligibleAnimals = Array.from(selectedAnimals).reduce<Array<AnimalApiId>>((acc, animalId) => {
        // Only include valid AnimalApiId values (aml_* prefix), not temporary IDs
        if (!isApiId('aml', animalId, false)) {
          return acc;
        }
        const animal = animalMap[animalId];
        if (_isNil(animal)) {
          return acc;
        }
        // Check if this sample definition exists in the animal's sample_groups
        if (animal.sample_groups?.some((animalSampleDef) => animalSampleDef.api_id === sampleDef.api_id) ?? false) {
          acc.push(animalId);
        }
        return acc;
      }, []);

      sampleDefinitionsMap[sampleDef.api_id] = eligibleAnimals;
    });

    executeCreateSamples({
      path: { studyApiId },
      body: {
        sample_definitions: sampleDefinitionsMap,
        date: null,
        comments: null,
      },
    })
      .then(() => {
        successToast('Planned samples created successfully!');
        handleCallback();
      })
      .catch(() => {
        errorToast('Failed to create planned samples. Please try again.');
      });
  }, [studyApiId, selectedAnimals, sampleDefinitions, executeCreateSamples, handleCallback, animalsData]);

  return (
    <ModalContainer size="wide">
      <ModalHeader
        title="Create planned samples"
        subText={
          <>
            Create samples for {sampleDefinitions.length} sample definition
            {sampleDefinitions.length === 1 ? '' : 's'}.
          </>
        }
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
      />
      {totalSamplesToCreate > 0 && (
        <div className="pl4 mt3">
          <Banner info className="mw6 mr4 mb1" dismiss={false}>
            <p className="lh-copy f6">
              <strong>
                {totalSamplesToCreate} planned sample{totalSamplesToCreate === 1 ? '' : 's'}
              </strong>{' '}
              will be created upon submission.
            </p>
          </Banner>
        </div>
      )}
      <div className="mh4 mt3">
        <div className="mb3">
          <AnimalSelectionTable
            studyApiId={studyApiId}
            onSelectionChange={setSelectedAnimals}
            sampleDefinitionIds={sampleDefinitions.reduce<Array<SampleDefinitionApiId>>(
              (acc, def) => (isApiId('sdf', def.api_id, false) ? [...acc, def.api_id] : acc),
              []
            )}
            onAnimalsDataChange={setAnimalsData}
            collectionType="planned"
          />
        </div>
        <div className="flex flex-row justify-start gap-h-3 pt3 pb2 mt3 bt">
          <Button
            primary
            onClick={handleCreateSamples}
            disabled={isCreating || selectedAnimals.size === 0}
            data-testid="create-samples-button"
          >
            {isCreating ? 'Creating...' : `Create`}
          </Button>
          <Button plain onClick={closeModal} disabled={isCreating} data-testid="cancel-create-samples-button">
            Cancel
          </Button>
        </div>
      </div>
    </ModalContainer>
  );
};
